import settings from '../settings';
import {
  UserSegmentType,
  IPaginationResponseDto,
  IUserSegment,
  ISegmentCurationStatus,
  ISegmentCurationType,
  ISegmentCurationUpdateRequestDto,
  IHomeCuration,
  HomeType,
} from '../types';
import { AxiosResponse, Canceler } from 'axios';
import api from './base';

export function getUserSegmentsWithTypes<T extends any>(types: UserSegmentType[]): [Promise<AxiosResponse<IPaginationResponseDto<T>>>, Canceler] {
  const uri = `${settings.getApiURI()}/segment-admin/v1/segments`;
  const params = { types };
  return api.getWithCancel(uri, { params });
}

export function getSimpleUserSegments<T extends any>(isActive?: boolean): [Promise<AxiosResponse<IPaginationResponseDto<T>>>, Canceler] {
  const uri = `${settings.getApiURI()}/segment-admin/v1/segments`;
  const params = { isSimpleVersion: true, isActive };
  return api.getWithCancel(uri, { params });
}

export function getUserSegmentsWithTypesAndPage<T extends any>(
  types?: UserSegmentType[],
  isActive = true,
  page = 1,
  limit = 10,
): [Promise<AxiosResponse<IPaginationResponseDto<T>>>, Canceler] {
  const uri = `${settings.getApiURI()}/segment-admin/v1/segments`;
  const params = { isActive, page, limit, types };

  return api.getWithCancel(uri, {
    params,
  });
}

export function getCuratedUserSegmentsWithTypesAndPage<T extends any>(
  homeType?: HomeType,
  isActive?: boolean,
  page?: number,
  limit?: number,
  searchKey?: string,
): [Promise<AxiosResponse<IPaginationResponseDto<T>>>, Canceler] {
  const uri = `${settings.getApiURI()}/segment-admin/v1/curated-segments`;
  const params = { homeType, isActive, searchKey, page, limit };

  return api.getWithCancel(uri, {
    params,
  });
}

export function searchUserSegments(
  types: UserSegmentType[],
  keyword: string,
  isActive?: boolean,
  page?: number,
  limit?: number,
  isSimpleVersion?: boolean,
): Promise<AxiosResponse<IPaginationResponseDto<IUserSegment>>> {
  const uri = `${settings.getApiURI()}/segment-admin/v1/segments`;
  return api.http.get(uri, { params: { searchKey: keyword, isActive, page, limit, types, isSimpleVersion } });
}

export function getUserSegmentCurationPreview(param: number | number[], homeType: HomeType): Promise<any> {
  if (typeof param === 'number') {
    const uri = `${settings.getApiURI()}/home/v2/preview`;
    return api.http.get(uri, { params: { userId: param, homeType } });
  } else {
    const uri = `${settings.getApiURI()}/home/v2/preview`;
    return api.http.get(uri, { params: { segmentIds: param, homeType } });
  }
}

export function getUserSegmentsCurations<T extends any>(
  status: ISegmentCurationStatus | 'all',
  type: ISegmentCurationType,
  page = 1,
  limit = 10,
): [Promise<AxiosResponse<IPaginationResponseDto<T>>>, Canceler] {
  const uri = `${settings.getApiURI()}/segment-admin/v1/home-curations`;
  const params = status !== 'all' ? { status, type, page, limit } : { type, page, limit };

  return api.getWithCancel(uri, {
    params,
  });
}

export function updateUserSegmentCuration(curationId: number, data: ISegmentCurationUpdateRequestDto) {
  const uri = `${settings.getApiURI()}/segment-admin/v1/home-curations/${curationId}`;
  return api.http.put(uri, data);
}

export function deleteUserSegmentCuration(curationId: number) {
  const uri = `${settings.getApiURI()}/segment-admin/v1/home-curations/${curationId}`;
  return api.http.delete(uri);
}

export function toggleActiveUserSegment(segmentId: string, type: string) {
  const uri = `${settings.getApiURI()}/user/v1/segments/${type}/${segmentId}/toggle`;

  return api.http.patch(uri);
}

export function getAutoUpdateUserSegmentQueryResult(sql: string) {
  const uri = `${settings.getApiURI()}/user/v1/segments/test`;

  return api.http.post(uri, {
    sql,
  });
}

export function getUserSegment(segmentId: number): [Promise<AxiosResponse<IUserSegment>>, Canceler] {
  const uri = `${settings.getApiURI()}/user/v1/segments/${segmentId}`;
  return api.getWithCancel(uri);
}

export function getUserSegmentCuration(segmentId: number): [Promise<AxiosResponse<IHomeCuration>>, Canceler] {
  const uri = `${settings.getApiURI()}/segment-admin/v1/segments/${segmentId}/home-curations`;
  return api.getWithCancel(uri);
}

export function saveUserSegment(segment: IUserSegment) {
  const uri = `${settings.getApiURI()}/user/v1/segments`;

  // FIXME: inconsistency between ResponseDto and RequestDto :,(
  return api.http.post(uri, segment);
}

export function saveUserSegmentCuration(segmentId: number, segmentCuration: IHomeCuration) {
  const uri = `${settings.getApiURI()}/segment-admin/v1/segments/${segmentId}/home-curations`;
  return api.http.patch(uri, segmentCuration);
}

export function editUserSegment(segmentId: number, segment: IUserSegment) {
  const uri = `${settings.getApiURI()}/user/v1/segments/${segmentId}`;

  return api.http.put(uri, { dte: String(segment.daysToExpiration), ...segment });
}

export function deleteUserSegment(segmentId: number) {
  const uri = `${settings.getApiURI()}/user/v1/segments/${segmentId}`;

  return api.http.delete(uri);
}

export function getHomeCurationPreview(homesetSplitIds: number[]) {
  const uri = `${settings.getApiURI()}/home/v2/preview`;

  return api.getWithCancel(uri, {
    params: {
      homesetSplitIds,
    },
  });
}
